import { useState } from 'react';
import type { Folder } from '../../services/files/filesService';
import type { DocumentTree, DocumentTreeNode } from '../../lib/documentTree';
import {
  CIME_DOSSIER_FILE_DRAG,
  dropContainsOsFiles,
  dropIsInternalFile,
  parseInternalFileDrag,
} from '../../lib/fileDrop';
import { cn } from '../../lib/cn';
import { ALL_DOCUMENTS_ID } from '../../lib/dossiers/documentNav';
import { VIRTUAL_EMAIL_ID, VIRTUAL_EMAIL_LABEL } from '../../lib/dossiers/virtualEmailFolder';
import { useFileTreeDrop } from './FileTreeDropContext';

function FaIcon({ name, className = 'w-4 h-4' }: { name: string; className?: string }) {
  return <img src={`/icons/${name}.svg`} alt="" className={className} aria-hidden />;
}

interface SidebarProps {
  tree: DocumentTree;
  selectedId: string;
  onSelect: (id: string) => void;
  expandedIds: Set<string>;
  onToggle: (folderId: string) => void;
  emailCount: number;
  allCount: number;
  canEdit: boolean;
  onRenameFolder: (folder: Folder) => void;
  onDeleteFolder: (folder: Folder) => void;
  onCreateSubfolder: (folder: Folder) => void;
}

export function DocumentFolderSidebar({
  tree,
  selectedId,
  onSelect,
  expandedIds,
  onToggle,
  emailCount,
  allCount,
  canEdit,
  onRenameFolder,
  onDeleteFolder,
  onCreateSubfolder,
}: SidebarProps) {
  return (
    <aside className="w-[250px] shrink-0 border-r border-[#e6e2d8] bg-[#fbfaf7]">
      {/* Le contenu reste visible quand la liste de droite défile. */}
      <div className="sticky top-[122px] flex max-h-[calc(100vh-210px)] flex-col">
      <div className="px-3.5 py-3 border-b border-cime-border">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-cime-text-muted">
          Arborescence
        </h2>
      </div>
      <nav className="flex-1 overflow-auto px-2 py-2" aria-label="Dossiers du dossier">
        <FlushItem
          id={ALL_DOCUMENTS_ID}
          icon="folders"
          label="Tous les documents"
          count={allCount}
          selected={selectedId === ALL_DOCUMENTS_ID}
          onSelect={onSelect}
        />
        <FlushItem
          id={VIRTUAL_EMAIL_ID}
          icon="envelope"
          label={VIRTUAL_EMAIL_LABEL}
          count={emailCount}
          selected={selectedId === VIRTUAL_EMAIL_ID}
          onSelect={onSelect}
        />
        <div className="h-px bg-cime-border my-2 mx-1.5" />
        {tree.rootFolders.map((node) => (
          <FolderNavNode
            key={node.folder.id}
            node={node}
            depth={0}
            selectedId={selectedId}
            onSelect={onSelect}
            expandedIds={expandedIds}
            onToggle={onToggle}
            canEdit={canEdit}
            onRenameFolder={onRenameFolder}
            onDeleteFolder={onDeleteFolder}
            onCreateSubfolder={onCreateSubfolder}
          />
        ))}
      </nav>
      </div>
    </aside>
  );
}

function FlushItem({
  id,
  icon,
  label,
  count,
  selected,
  onSelect,
}: {
  id: string;
  icon: string;
  label: string;
  count: number;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(id)}
      className={cn(
        'flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-md',
        selected ? 'bg-cime-primary/10 font-semibold text-cime-primary' : 'hover:bg-cime-primary/5',
      )}
    >
      <FaIcon name={icon} />
      <span className="flex-1 min-w-0 text-sm truncate">{label}</span>
      <span className="text-[11px] tabular-nums text-cime-text-muted">{count}</span>
    </button>
  );
}

function FolderNavNode({
  node,
  depth,
  selectedId,
  onSelect,
  expandedIds,
  onToggle,
  canEdit,
  onRenameFolder,
  onDeleteFolder,
  onCreateSubfolder,
}: {
  node: DocumentTreeNode;
  depth: number;
  selectedId: string;
  onSelect: (id: string) => void;
  expandedIds: Set<string>;
  onToggle: (folderId: string) => void;
  canEdit: boolean;
  onRenameFolder: (folder: Folder) => void;
  onDeleteFolder: (folder: Folder) => void;
  onCreateSubfolder: (folder: Folder) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const dropCtx = useFileTreeDrop();
  const expanded = expandedIds.has(node.folder.id);
  const selected = selectedId === node.folder.id;
  const hasKids = node.children.length > 0;
  const dropEnabled = canEdit && !!dropCtx?.enabled;

  function handleDragOver(e: React.DragEvent) {
    if (!dropEnabled) return;
    const types = Array.from(e.dataTransfer.types);
    if (!dropIsInternalFile(types) && !dropContainsOsFiles(types)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = dropIsInternalFile(types) ? 'move' : 'copy';
    setDropActive(true);
  }

  function handleDrop(e: React.DragEvent) {
    if (!dropEnabled || !dropCtx) return;
    e.preventDefault();
    e.stopPropagation();
    setDropActive(false);
    const types = Array.from(e.dataTransfer.types);
    if (dropIsInternalFile(types)) {
      const payload = parseInternalFileDrag(
        e.dataTransfer.getData(CIME_DOSSIER_FILE_DRAG) || e.dataTransfer.getData('text/plain'),
      );
      if (payload) dropCtx.onInternalDrop(payload.fileId, payload.fromFolderId, node.folder.id);
      return;
    }
    const files = Array.from(e.dataTransfer.files);
    if (files.length) dropCtx.onOsDrop(node.folder.id, files);
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropActive(false);
      }}
      onDrop={handleDrop}
    >
      <div
        className={cn('flex items-center gap-0.5', dropActive && 'bg-cime-primary/10 ring-1 ring-inset ring-cime-primary rounded-md')}
        style={{ paddingLeft: depth * 16 }}
      >
        {hasKids ? (
          <button
            type="button"
            onClick={() => onToggle(node.folder.id)}
            className="w-[18px] h-[18px] shrink-0 grid place-items-center rounded hover:bg-cime-primary/10"
            aria-expanded={expanded}
            aria-label={expanded ? `Replier ${node.folder.name}` : `Déplier ${node.folder.name}`}
          >
            <img
              src="/icons/chevron-right.svg"
              alt=""
              className={cn('w-2.5 h-2.5 transition-transform', expanded && 'rotate-90')}
            />
          </button>
        ) : (
          <span className="w-[18px] h-[18px] shrink-0" />
        )}
        <button
          type="button"
          onClick={() => onSelect(node.folder.id)}
          className={cn(
            'flex items-center gap-2 flex-1 min-w-0 text-left px-2 py-1.5 rounded-md',
            selected ? 'bg-cime-primary/10 font-semibold text-cime-primary' : 'hover:bg-cime-primary/5',
          )}
        >
          <FaIcon name={expanded && hasKids ? 'folder-open' : 'folder'} />
          <span className="flex-1 min-w-0 text-sm truncate">{node.folder.name}</span>
          <span className="text-[11px] tabular-nums text-cime-text-muted">{node.fileCount}</span>
        </button>
        {canEdit && (
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="p-1 rounded hover:bg-cime-primary/10"
              aria-label={`Actions ${node.folder.name}`}
            >
              <FaIcon name="ellipsis-vertical" className="w-3.5 h-3.5" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 z-10 min-w-[180px] rounded-md border border-neutral-200 bg-white shadow-lg py-1">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onCreateSubfolder(node.folder); }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-neutral-50 text-left"
                >
                  <FaIcon name="folder-plus" className="w-3.5 h-3.5" /> Nouveau sous-dossier
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onRenameFolder(node.folder); }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-neutral-50 text-left"
                >
                  <FaIcon name="pencil" className="w-3.5 h-3.5" /> Renommer
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onDeleteFolder(node.folder); }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-neutral-50 text-left text-red-600"
                >
                  <FaIcon name="trash-can" className="w-3.5 h-3.5" /> Supprimer
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {expanded && hasKids && node.children.map((child) => (
        <FolderNavNode
          key={child.folder.id}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          expandedIds={expandedIds}
          onToggle={onToggle}
          canEdit={canEdit}
          onRenameFolder={onRenameFolder}
          onDeleteFolder={onDeleteFolder}
          onCreateSubfolder={onCreateSubfolder}
        />
      ))}
    </div>
  );
}
